/**
 * Наборы символов режимов.
 *
 * Состав каждого набора выводится из данных, а не перечисляется в коде:
 * цифры и специальные символы — из раскладки, знаки препинания — из тех, что
 * действительно встречаются в наборе предложений языка. Список в коде
 * разошёлся бы и с раскладкой, и со словарём при первом же их изменении.
 *
 * Наборы не пересекаются: знак, встречающийся в предложениях, тренируется
 * режимом пунктуации, а не режимом специальных символов.
 *
 * Три режима и три требования: цифровой ряд группами по парам одноимённых
 * пальцев (ТЗ FR-EN-12), знаки препинания внутри естественных предложений, а
 * не в отрыве от текста (ТЗ FR-EN-13), и всё остальное — что не буква, не
 * цифра и не знак (ТЗ FR-EN-14).
 */

import { getLayout, type KeyDefinition } from '@type2x/keyboard-layouts';
import { getSentences } from '@type2x/corpora';

import type { SymbolMode, SymbolsSnapshot } from '../contracts/symbols.js';
import { PLATFORM_PRIORITY_SYMBOLS } from '../contracts/symbols.js';

/** Цифра ли это. */
function isDigit(character: string): boolean {
  return /^\p{Nd}$/u.test(character);
}

/** Буква ли это: буквам учат «Основы», а не эти режимы. */
function isLetter(character: string): boolean {
  return /^\p{L}$/u.test(character);
}

/** Цифры раскладки в порядке следования клавиш слева направо. */
export function digitCharacters(snapshot: SymbolsSnapshot): string[] {
  return getLayout(snapshot.layoutId, snapshot.platform)
    .keys.filter((key) => key.row === 'number' && isDigit(key.base))
    .sort((left, right) => left.column - right.column)
    .map((key) => key.base);
}

/** Символы раскладки: и без Shift, и с ним. */
function layoutCharacters(snapshot: SymbolsSnapshot): KeyDefinition[] {
  return [...getLayout(snapshot.layoutId, snapshot.platform).keys].sort(
    (left, right) => left.row.localeCompare(right.row) || left.column - right.column,
  );
}

/**
 * Знаки препинания языка в порядке убывания частоты в предложениях.
 *
 * Знак, которого нет в наборе предложений, тренировать нечем: упражнение
 * режима — естественный текст, а выдумывать его нельзя (ТЗ §8.3). Знак,
 * которого нет в раскладке, пользователь набрать не сможет.
 */
export function punctuationCharacters(snapshot: SymbolsSnapshot): string[] {
  const typable = new Set(
    getLayout(snapshot.layoutId, snapshot.platform).keys.flatMap((key) => [key.base, key.shifted]),
  );
  const counts = new Map<string, number>();

  for (const sentence of getSentences(snapshot.language)) {
    for (const character of sentence.text) {
      if (!/^\p{P}$/u.test(character) || !typable.has(character)) continue;
      counts.set(character, (counts.get(character) ?? 0) + 1);
    }
  }

  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .map(([character]) => character);
}

/**
 * Специальные символы: всё, что не буква, не цифра и не знак препинания.
 *
 * Порядок зависит от операционной системы: символы, которые её пользователь
 * набирает каждый день, открываются первыми (ТЗ §6, режим 10).
 */
export function specialCharacters(snapshot: SymbolsSnapshot): string[] {
  const punctuation = new Set(punctuationCharacters(snapshot));
  const seen = new Set<string>();
  const rest: string[] = [];

  for (const key of layoutCharacters(snapshot)) {
    for (const character of [key.base, key.shifted]) {
      if (character === '' || character === ' ') continue;
      if (isLetter(character) || isDigit(character)) continue;
      if (punctuation.has(character) || seen.has(character)) continue;

      seen.add(character);
      rest.push(character);
    }
  }

  const priority = PLATFORM_PRIORITY_SYMBOLS[snapshot.platform].filter((character) =>
    seen.has(character),
  );
  const priorityIndex = new Set(priority);

  return [...priority, ...rest.filter((character) => !priorityIndex.has(character))];
}

/** Символы режима в порядке открытия. */
export function modeCharacters(snapshot: SymbolsSnapshot, mode: SymbolMode): string[] {
  switch (mode) {
    case 'digits':
      return digitCharacters(snapshot);
    case 'punctuation':
      return punctuationCharacters(snapshot);
    case 'special-characters':
      return specialCharacters(snapshot);
  }
}
